// external imports
import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  Checkbox,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { PersonAddAlt1, SwapHoriz, Groups, CheckCircle, DeleteOutline } from '@mui/icons-material';

// derive up-to-two-letter initials from a full name
const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();

const ClusterDetails = ({ manager, onAddTrainer, onReassignAll, onMove, onDeleteTrainer }) => {
  const trainers = React.useMemo(() => manager?.trainers ?? [], [manager]);

  // selected trainer ids (by empId)
  const [selectedIds, setSelectedIds] = React.useState([]);

  // reset selection when the active manager changes or the trainer list refreshes
  React.useEffect(() => {
    setSelectedIds([]);
  }, [manager?.empId, trainers]);

  const allSelected = trainers.length > 0 && selectedIds.length === trainers.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : trainers.map((t) => t.empId));
  };

  const toggleOne = (empId) => {
    setSelectedIds((prev) =>
      prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
    );
  };

  // empty state — no manager chosen yet
  if (!manager) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          background: '#ffffff',
          borderRadius: '8px',
          p: 2,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Groups sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
            Select a cluster manager to view their trainers.
          </Typography>
        </Box>
      </Box>
    );
  }

  const headCellSx = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.04em',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    py: 1.25,
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      {/* ---- Header ---- */}
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#7c3aed', width: 64, height: 64, fontSize: 22, fontWeight: 600 }}>
            {getInitials(manager.fullName)}
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
                {manager.fullName}
              </Typography>
              <Chip
                label={manager.empId}
                size="small"
                sx={{
                  bgcolor: '#dbeafe',
                  color: '#2563eb',
                  fontWeight: 600,
                  fontSize: 12,
                  height: 22,
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 14.5, color: '#475569', mt: 0.25 }}>
              {`${manager.designation} · ${manager.location}`}
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#64748b', mt: 0.25 }}>
              {[manager.email, manager.phone].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1 />}
            onClick={onAddTrainer}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5 }}
          >
            Add Trainer
          </Button>
          <Button
            variant="outlined"
            startIcon={<SwapHoriz />}
            onClick={onReassignAll}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5 }}
          >
            Reassign All
          </Button>
        </Box>

        {/* Regions + trainer count */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mt: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', color: '#94a3b8' }}
            >
              REGION:
            </Typography>
            {manager.regions.map((region) => (
              <Chip
                key={region}
                label={region}
                size="small"
                sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 500, fontSize: 13 }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#2563eb' }}>
            <Groups sx={{ fontSize: 20 }} />
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              {`${trainers.length} trainers`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ---- Selection bar ---- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          px: 2.5,
          py: 1.5,
          background: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ fontSize: 20, color: selectedIds.length ? '#2563eb' : '#cbd5e1' }} />
          <Typography sx={{ fontSize: 14, color: '#334155' }}>
            <b>{selectedIds.length}</b> selected
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SwapHoriz />}
            disabled={selectedIds.length === 0}
            onClick={() => onMove?.(selectedIds)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2 }}
          >
            Move to another CM
          </Button>
          <Button
            size="small"
            disabled={selectedIds.length === 0}
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* ---- Trainers table ---- */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #e2e8f0' }}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell sx={headCellSx}>EMP ID</TableCell>
              <TableCell sx={headCellSx}>FULL NAME</TableCell>
              <TableCell sx={headCellSx}>DESIGNATION</TableCell>
              <TableCell sx={headCellSx}>REGION</TableCell>
              <TableCell sx={headCellSx} align="center">
                DEL
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {trainers.map((trainer) => {
              const isChecked = selectedIds.includes(trainer.empId);
              return (
                <TableRow
                  key={trainer.empId}
                  hover
                  sx={{ background: isChecked ? '#eff6ff' : 'transparent' }}
                >
                  <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => toggleOne(trainer.empId)}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#2563eb' }}>
                      {trainer.empId}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                      {trainer.fullName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: 14, color: '#475569' }}>
                      {trainer.designation}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {(trainer.regions || []).map((region) => (
                        <Chip
                          key={region}
                          label={region}
                          size="small"
                          sx={{ bgcolor: '#f1f5f9', color: '#334155', fontSize: 12.5 }}
                        />
                      ))}
                      {(!trainer.regions || trainer.regions.length === 0) && (
                        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>—</Typography>
                      )}
                    </Box>
                  </TableCell>
                  {/* <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                    {trainer.branchName ? (
                      <Chip
                        label={trainer.branchName}
                        size="small"
                        sx={{
                          bgcolor: '#dcfce7',
                          color: '#15803d',
                          fontWeight: 500,
                          fontSize: 12.5,
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>—</Typography>
                    )}
                  </TableCell> */}
                  <TableCell align="center" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                    <IconButton
                      size="small"
                      onClick={() => onDeleteTrainer?.(trainer.empId)}
                      sx={{ color: '#ef4444' }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {trainers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                  <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
                    No trainers mapped to this cluster manager.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default ClusterDetails;
